import React from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
  ComposedChart, Scatter, Legend,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

// ── Design tokens (from design-system.md) ────────────────────────────────────
const CATEGORICAL = ['#4e79a7','#f28e2c','#e15759','#76b7b2','#59a14f','#edc949','#af7aa1','#ff9da7','#9c755f','#bab0ab'];
const hashColor = (name: string) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return CATEGORICAL[Math.abs(h) % CATEGORICAL.length];
};
const TEXT_PRIMARY   = '#1a1a2e';
const TEXT_SECONDARY = '#6c757d';
const TEXT_MUTED     = '#9e9e9e';
const BORDER         = '#e9ecef';
const GRIDLINE       = '#f0f0f0';
const CARD_STYLE: React.CSSProperties = {
  background: '#fff', borderRadius: '12px', border: `1px solid ${BORDER}`,
  padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};
const TOOLTIP_CONTENT_STYLE: React.CSSProperties = {
  background: '#1a1a2e', border: 'none', borderRadius: '8px',
  color: '#f8f9fa', fontSize: '12px', padding: '10px 14px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
};

// ── Formatters ────────────────────────────────────────────────────────────────
const formatters: Record<string, (v: number) => string> = {
  usd:    (v) => { const a = Math.abs(v); const s = v < 0 ? '-' : ''; return a >= 1e9 ? `${s}$${(a/1e9).toFixed(1)}B` : a >= 1e6 ? `${s}$${(a/1e6).toFixed(1)}M` : a >= 1e3 ? `${s}$${(a/1e3).toFixed(0)}K` : `${s}$${a.toFixed(0)}`; },
  count:  (v) => { const a = Math.abs(v); const s = v < 0 ? '-' : ''; return a >= 1e6 ? `${s}${(a/1e6).toFixed(1)}M` : a >= 1e3 ? `${s}${(a/1e3).toFixed(1)}K` : v.toLocaleString(); },
  pct:    (v) => `${v.toFixed(1)}%`,
  number: (v) => v.toLocaleString(),
};
const fmt = (value: number, format = 'number') => (formatters[format] || formatters.number)(value);

const CAT_AXIS_CHAR_PX = 6.2;
const CAT_AXIS_MIN_W = 80;
const CAT_AXIS_MAX_W = 150;
const categoryAxisWidth = (labels: string[]) => {
  const longest = labels.reduce((m, l) => Math.max(m, String(l ?? '').length), 0);
  return Math.min(CAT_AXIS_MAX_W, Math.max(CAT_AXIS_MIN_W, Math.round(longest * CAT_AXIS_CHAR_PX) + 16));
};
const TruncatedYAxisTick: React.FC<any> = ({ x, y, payload, axisWidth }) => {
  const full = String(payload?.value ?? '');
  const maxChars = Math.max(6, Math.floor((axisWidth - 12) / CAT_AXIS_CHAR_PX));
  const shown = full.length > maxChars ? `${full.slice(0, maxChars - 1)}…` : full;
  return (
    <text x={x} y={y} dy={4} textAnchor="end" fontSize={11} fill={TEXT_SECONDARY}>
      {shown !== full && <title>{full}</title>}
      {shown}
    </text>
  );
};

// ── Types ─────────────────────────────────────────────────────────────────────
interface KPISpec {
  label: string;
  value: number;
  format?: string;
  delta?: number | null;
  deltaLabel?: string;
  isPositiveGood?: boolean;
}

interface BulletMetric {
  label: string;
  actual: number;
  target: number;
  max: number;
  format?: string;
  isPositiveGood?: boolean;
}

interface SparklineRow {
  label: string;
  value: string;
  vsTarget?: number;
  vsPrior?: number;
  sparkData?: { v: number }[];
  isPositiveGood?: boolean;
}

export interface ChartSpec {
  type: 'bar' | 'line' | 'donut' | 'pop_bar' | 'stacked_bar' | 'waterfall' | 'heatmap' | 'scatter' | 'bullet' | 'sparkline_table';
  title: string;
  subtitle?: string;
  horizontal?: boolean;
  xKey?: string;
  yKey?: string;
  format?: string;
  data: { name?: string; value?: number; [k: string]: unknown }[];
  centerValue?: string;
  centerLabel?: string;
  // pop_bar
  currentKey?: string;
  priorKey?: string;
  currentLabel?: string;
  priorLabel?: string;
  // stacked_bar
  keys?: string[];
  // heatmap
  rowLabels?: string[];
  colLabels?: string[];
  heatData?: (number | null)[][];
  // scatter
  xLabel?: string;
  yLabel?: string;
  // bullet
  metrics?: BulletMetric[];
  // sparkline_table
  rows?: SparklineRow[];
  // drill-down: AI provides the column name it grouped by; frontend filters rawData
  filterColumn?: string;
  filterType?: 'exact' | 'year' | 'month';
}

interface InsightSpec {
  what: string;
  soWhat: string;
  nowWhat: string;
}

export interface RawDataSheet {
  sheetName: string;
  headers: string[];
  rows: unknown[][];
  totalRows: number;
}

export interface RawDataFile {
  fileName: string;
  sheets: RawDataSheet[];
}

export interface DashboardSpecType {
  title: string;
  subtitle?: string;
  summary?: { label: string; value: string }[];
  kpis?: KPISpec[];
  charts?: ChartSpec[];
  insights?: InsightSpec[];
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
const KPICard: React.FC<KPISpec> = ({ label, value, format = 'number', delta, deltaLabel, isPositiveGood = true }) => {
  const isPositive = (delta ?? 0) >= 0;
  const isGood     = isPositiveGood ? isPositive : !isPositive;
  const deltaColor = delta == null ? TEXT_SECONDARY : isGood ? '#2ecc71' : '#e74c3c';
  const deltaBg    = delta == null ? '#f8f9fa'       : isGood ? '#d4edda' : '#f8d7da';
  const DeltaIcon  = delta == null ? Minus : isPositive ? TrendingUp : TrendingDown;

  return (
    <div style={{ ...CARD_STYLE, display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }}>
      <div style={{ fontSize: '11px', fontWeight: 500, color: TEXT_SECONDARY, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{ fontSize: '36px', fontWeight: 700, color: TEXT_PRIMARY, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
        {fmt(value, format)}
      </div>
      {delta != null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: deltaBg, color: deltaColor, borderRadius: '4px', padding: '2px 7px', fontSize: '11px', fontWeight: 600 }}>
            <DeltaIcon size={10} />
            {Math.abs(delta).toFixed(1)}%
          </span>
          {deltaLabel && <span style={{ fontSize: '11px', color: TEXT_MUTED }}>{deltaLabel}</span>}
        </div>
      )}
    </div>
  );
};

// ── Chart title block ─────────────────────────────────────────────────────────
const ChartHeader: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={{ fontSize: '15px', fontWeight: 600, color: TEXT_PRIMARY }}>{title}</div>
    {subtitle && <div style={{ fontSize: '12px', color: TEXT_SECONDARY, marginTop: '2px' }}>{subtitle}</div>}
  </div>
);

// ── Custom tooltip ────────────────────────────────────────────────────────────
const CustomTooltip: React.FC<{ active?: boolean; payload?: { name: string; value: number; color?: string }[]; label?: string; format?: string }> =
  ({ active, payload, label, format }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={TOOLTIP_CONTENT_STYLE}>
        {label && <div style={{ fontWeight: 600, marginBottom: '6px', color: '#f8fafc' }}>{label}</div>}
        {payload.map((p, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: p.color || '#7c3aed', flexShrink: 0 }} />
            <span style={{ color: '#cbd5e1' }}>{p.name}:</span>
            <strong style={{ color: '#f8fafc' }}>{fmt(p.value, format)}</strong>
          </div>
        ))}
      </div>
    );
  };

// ── Drill-down Modal ──────────────────────────────────────────────────────────
interface DrillDownData { title: string; columns: string[]; rows: unknown[][] }
const DrillDownModal: React.FC<{ data: DrillDownData; onClose: () => void }> = ({ data, onClose }) => {
  const [copied, setCopied] = React.useState(false);

  const toCSV = () => {
    const header = data.columns.join(',');
    const rowLines = data.rows.map(row =>
      (row as unknown[]).map(cell => {
        const v = cell == null ? '' : String(cell);
        return v.includes(',') || v.includes('"') || v.includes('\n') ? `"${v.replace(/"/g, '""')}"` : v;
      }).join(',')
    );
    return [header, ...rowLines].join('\n');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(toCSV()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    const csv = toCSV();
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${data.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
      onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxWidth: '900px', width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e1b4b' }}>{data.title}</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{data.rows.length} records</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={handleCopy} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, background: copied ? '#ecfdf5' : '#f5f3ff', color: copied ? '#059669' : '#5b21b6', border: `1px solid ${copied ? '#6ee7b7' : '#ddd6fe'}`, borderRadius: '6px', cursor: 'pointer' }}>
              {copied ? '✓ Copied' : '⎘ Copy CSV'}
            </button>
            <button onClick={handleDownload} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, background: '#f5f3ff', color: '#5b21b6', border: '1px solid #ddd6fe', borderRadius: '6px', cursor: 'pointer' }}>
              ↓ Download CSV
            </button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#6b7280', lineHeight: 1, padding: '4px' }}>×</button>
          </div>
        </div>
        {/* Table */}
        <div style={{ overflow: 'auto', flex: 1 }}>
          {data.columns.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#f5f3ff', position: 'sticky', top: 0 }}>
                  {data.columns.map((col, i) => (
                    <th key={i} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#5b21b6', borderBottom: '2px solid #ddd6fe', whiteSpace: 'nowrap' }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, ri) => (
                  <tr key={ri} style={{ background: ri % 2 === 0 ? '#fff' : '#faf9ff', borderBottom: '1px solid #f3f4f6' }}>
                    {(row as unknown[]).map((cell, ci) => (
                      <td key={ci} style={{ padding: '8px 12px', color: '#374151', whiteSpace: 'nowrap', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {cell == null ? '—' : String(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>No detail data available for this segment.</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Bar Chart ─────────────────────────────────────────────────────────────────
const BarChartCard: React.FC<{ spec: ChartSpec; onDrilldown?: (label: string) => void | Promise<void> }> = ({ spec, onDrilldown }) => {
  const { title, subtitle, data, horizontal, yKey = 'value', xKey = 'name' } = spec;
  const format = spec.format || (spec.data[0] && Number(spec.data[0].value) > 1000 ? 'usd' : 'number');
  const color = CATEGORICAL[0];

  const handleClick = (payload: any) => {
    if (!onDrilldown) return;
    const label = payload?.activeLabel || payload?.activePayload?.[0]?.payload?.name || payload?.name;
    if (label) onDrilldown(String(label));
  };

  const yAxisW = categoryAxisWidth(data.map(d => String(d[xKey] ?? '')));
  const maxVal = Math.max(0, ...data.map(d => Number(d[yKey] ?? 0)));
  const xDomainMax = maxVal > 0 ? Math.ceil(maxVal * 1.15) : 1;

  return (
    <div style={CARD_STYLE}>
      <ChartHeader title={title} subtitle={subtitle} />
      {onDrilldown && (
        <div style={{ fontSize: '10px', color: '#7c3aed', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>↓</span> Click any bar to drill down
        </div>
      )}
      <ResponsiveContainer width="100%" height={Math.max(260, data.length * (horizontal ? 36 : 50))}>
        {horizontal ? (
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 64, bottom: 4, left: 8 }} onClick={handleClick} style={{ cursor: onDrilldown ? 'pointer' : 'default' }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRIDLINE} horizontal={false} />
            <XAxis type="number" domain={[0, xDomainMax]} tick={{ fontSize: 11, fill: TEXT_SECONDARY }} axisLine={false} tickLine={false} tickFormatter={(v) => fmt(v, format)} />
            <YAxis type="category" dataKey={xKey} axisLine={false} tickLine={false} width={yAxisW} interval={0} tick={<TruncatedYAxisTick axisWidth={yAxisW} />} />
            <Tooltip content={<CustomTooltip format={format} />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
            <Bar dataKey={yKey} fill={color} minPointSize={2} radius={[0, 4, 4, 0]} name={title}>
              <LabelList dataKey={yKey} position="right" style={{ fontSize: '10px', fill: TEXT_SECONDARY }} formatter={(v: number) => fmt(v, format)} />
            </Bar>
          </BarChart>
        ) : (
          <BarChart data={data} margin={{ top: 16, right: 24, bottom: 8, left: 8 }} barCategoryGap="35%" onClick={handleClick} style={{ cursor: onDrilldown ? 'pointer' : 'default' }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRIDLINE} vertical={false} />
            <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: TEXT_SECONDARY }} axisLine={{ stroke: BORDER }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: TEXT_SECONDARY }} axisLine={false} tickLine={false} tickFormatter={(v) => fmt(v, format)} width={52} />
            <Tooltip content={<CustomTooltip format={format} />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
            <Bar dataKey={yKey} fill={color} radius={[4, 4, 0, 0]} name={title}>
              <LabelList dataKey={yKey} position="top" style={{ fontSize: '10px', fill: TEXT_SECONDARY }} formatter={(v: number) => fmt(v, format)} />
            </Bar>
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

// ── Line Chart ────────────────────────────────────────────────────────────────
const LineChartCard: React.FC<{ spec: ChartSpec }> = ({ spec }) => {
  const { title, subtitle, data, yKey = 'value', xKey = 'name' } = spec;
  const maxVal = Math.max(...data.map(d => Number(d[yKey] ?? 0)));
  const format = spec.format || (maxVal > 1000 ? 'usd' : 'number');

  return (
    <div style={CARD_STYLE}>
      <ChartHeader title={title} subtitle={subtitle} />
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={data} margin={{ top: 16, right: 40, bottom: 8, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRIDLINE} vertical={false} />
          <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: TEXT_SECONDARY }} axisLine={{ stroke: BORDER }} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: TEXT_SECONDARY }} axisLine={false} tickLine={false} tickFormatter={(v) => fmt(v, format)} width={56} />
          <Tooltip content={<CustomTooltip format={format} />} />
          <Line type="monotone" dataKey={yKey} stroke={CATEGORICAL[0]} strokeWidth={3} dot={false}
            activeDot={{ r: 5, fill: CATEGORICAL[0], stroke: '#fff', strokeWidth: 2 }} name={title} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// ── Donut Chart ───────────────────────────────────────────────────────────────
const DonutChartCard: React.FC<{ spec: ChartSpec; onDrilldown?: (label: string) => void | Promise<void> }> = ({ spec, onDrilldown }) => {
  const { title, subtitle, data, centerValue, centerLabel } = spec;
  const total = data.reduce((s, d) => s + (d.value ?? 0), 0);
  const maxVal = Math.max(...data.map(d => d.value ?? 0));
  const format = spec.format || (maxVal > 1000 ? 'usd' : 'number');

  const DonutTooltip: React.FC<{ active?: boolean; payload?: { name: string; value: number; color?: string }[] }> = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0];
    const pct = (item.value / (total || 1) * 100).toFixed(1);
    return (
      <div style={TOOLTIP_CONTENT_STYLE}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.color || '#7c3aed', flexShrink: 0 }} />
          <span style={{ fontWeight: 600, color: '#f8fafc' }}>{item.name}</span>
        </div>
        <div style={{ color: '#cbd5e1', paddingLeft: '14px' }}>{pct}% · <strong style={{ color: '#f8fafc' }}>{fmt(item.value, format)}</strong></div>
      </div>
    );
  };

  const handlePieClick = (data: any) => {
    if (!onDrilldown) return;
    if (data?.name) onDrilldown(String(data.name));
  };

  return (
    <div style={CARD_STYLE}>
      <ChartHeader title={title} subtitle={subtitle} />
      {onDrilldown && (
        <div style={{ fontSize: '10px', color: '#7c3aed', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>↓</span> Click any segment to drill down
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
        <div style={{ position: 'relative', width: 180, height: 180, flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={2} dataKey="value" startAngle={90} endAngle={-270} onClick={handlePieClick} style={{ cursor: onDrilldown ? 'pointer' : 'default' }}>
                {data.map((entry, i) => <Cell key={i} fill={hashColor(String(entry.name ?? i))} />)}
              </Pie>
              <Tooltip content={<DonutTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <div style={{ fontSize: '20px', fontWeight: 700, color: TEXT_PRIMARY, lineHeight: 1 }}>{centerValue || fmt(total, format)}</div>
            <div style={{ fontSize: '10px', color: TEXT_SECONDARY, marginTop: '2px' }}>{centerLabel || 'Total'}</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: 0 }}>
          {data.map((item, i) => {
            const pct = ((item.value ?? 0) / (total || 1) * 100).toFixed(1);
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', minHeight: '28px', padding: '4px 0' }}>
                <div style={{ width: 10, height: 10, borderRadius: '2px', background: hashColor(String(item.name ?? i)), flexShrink: 0 }} />
                <div style={{ fontSize: '12px', lineHeight: '24px', color: TEXT_PRIMARY, flex: 1, textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingBottom: '2px' }}>{item.name}</div>
                <div style={{ fontSize: '12px', lineHeight: '24px', fontWeight: 600, color: TEXT_PRIMARY, flexShrink: 0, paddingBottom: '2px' }}>{pct}%</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── Period-over-Period Bar Chart ──────────────────────────────────────────────
const PoPBarChartCard: React.FC<{ spec: ChartSpec }> = ({ spec }) => {
  const { title, subtitle, data, currentKey = 'current', priorKey = 'prior',
    currentLabel = 'Current Period', priorLabel = 'Prior Period' } = spec;
  const fmtKey = spec.format || 'number';
  const fmtFn = (v: number) => fmt(v, fmtKey);

  return (
    <div style={CARD_STYLE}>
      <ChartHeader title={title} subtitle={subtitle} />
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={data} margin={{ top: 16, right: 24, bottom: 8, left: 8 }} barCategoryGap="30%" barGap={3}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRIDLINE} vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: TEXT_SECONDARY }} axisLine={{ stroke: BORDER }} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: TEXT_SECONDARY }} axisLine={false} tickLine={false} tickFormatter={fmtFn} width={56} />
          <Tooltip content={<CustomTooltip format={fmtKey} />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
          <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
          <Bar dataKey={priorKey} name={priorLabel} fill="#bab0ab" radius={[3, 3, 0, 0]} />
          <Bar dataKey={currentKey} name={currentLabel} fill={CATEGORICAL[0]} radius={[3, 3, 0, 0]}>
            <LabelList dataKey={currentKey} position="top" style={{ fontSize: '10px', fill: TEXT_SECONDARY }} formatter={fmtFn} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ── Stacked Bar Chart ─────────────────────────────────────────────────────────
const StackedBarChartCard: React.FC<{ spec: ChartSpec; onDrilldown?: (label: string) => void | Promise<void> }> = ({ spec, onDrilldown }) => {
  const { title, subtitle, data, keys = [] } = spec;
  const fmtKey = spec.format || 'number';
  const fmtFn = (v: number) => fmt(v, fmtKey);

  const handleClick = (payload: any) => {
    if (!onDrilldown) return;
    const label = payload?.activeLabel || payload?.activePayload?.[0]?.payload?.name;
    if (label) onDrilldown(String(label));
  };

  return (
    <div style={CARD_STYLE}>
      <ChartHeader title={title} subtitle={subtitle} />
      {onDrilldown && (
        <div style={{ fontSize: '10px', color: '#7c3aed', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>↓</span> Click any bar to drill down
        </div>
      )}
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={data} margin={{ top: 8, right: 24, bottom: 8, left: 8 }} barCategoryGap="30%" onClick={handleClick} style={{ cursor: onDrilldown ? 'pointer' : 'default' }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRIDLINE} vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: TEXT_SECONDARY }} axisLine={{ stroke: BORDER }} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: TEXT_SECONDARY }} axisLine={false} tickLine={false} tickFormatter={fmtFn} width={56} />
          <Tooltip content={<CustomTooltip format={fmtKey} />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
          <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
          {keys.map((k, i) => (
            <Bar key={k} dataKey={k} stackId="stack" fill={hashColor(k)}
              radius={i === keys.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ── Waterfall Chart ───────────────────────────────────────────────────────────
const WaterfallChartCard: React.FC<{ spec: ChartSpec; onDrilldown?: (label: string) => void | Promise<void> }> = ({ spec, onDrilldown }) => {
  const { title, subtitle, data } = spec;
  const fmtKey = spec.format || 'number';
  const fmtFn = (v: number) => fmt(v, fmtKey);

  let running = 0;
  const bars = data.map((item, i) => {
    const isStart = i === 0;
    const isEnd = i === data.length - 1;
    const isTotal = isStart || isEnd;
    const val = Number(item.value ?? 0);
    const base = isTotal ? 0 : running;
    if (!isEnd) running += val;
    return { ...item, base, displayValue: isEnd ? running : val, isTotal, isPositive: val >= 0 };
  });

  const handleClick = (payload: any) => {
    if (!onDrilldown) return;
    const label = payload?.activeLabel || payload?.activePayload?.[0]?.payload?.name;
    if (label) onDrilldown(String(label));
  };

  return (
    <div style={CARD_STYLE}>
      <ChartHeader title={title} subtitle={subtitle} />
      {onDrilldown && (
        <div style={{ fontSize: '10px', color: '#7c3aed', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>↓</span> Click any bar to drill down
        </div>
      )}
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={bars} margin={{ top: 20, right: 24, bottom: 8, left: 8 }} barCategoryGap="28%" onClick={handleClick}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRIDLINE} vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: TEXT_SECONDARY }} axisLine={{ stroke: BORDER }} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: TEXT_SECONDARY }} axisLine={false} tickLine={false} tickFormatter={fmtFn} width={56} />
          <Tooltip
            cursor={{ fill: 'rgba(0,0,0,0.03)' }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const entry = payload[0]?.payload as { name: string; displayValue: number; isPositive: boolean; isTotal: boolean };
              const color = entry.isTotal ? CATEGORICAL[0] : entry.isPositive ? '#2ecc71' : '#e74c3c';
              return (
                <div style={TOOLTIP_CONTENT_STYLE}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0 }} />
                    <span style={{ color: '#cbd5e1' }}>{entry.name}:</span>
                    <strong style={{ color: '#f8fafc' }}>{fmtFn(entry.displayValue)}</strong>
                  </div>
                </div>
              );
            }}
          />
          <Bar dataKey="base" stackId="wf" fill="transparent" />
          <Bar dataKey="displayValue" stackId="wf" radius={[3, 3, 0, 0]}>
            {bars.map((entry, i) => (
              <Cell key={i} fill={entry.isTotal ? CATEGORICAL[0] : entry.isPositive ? '#2ecc71' : '#e74c3c'} />
            ))}
            <LabelList dataKey="displayValue" position="top" style={{ fontSize: '10px', fill: TEXT_SECONDARY }} formatter={fmtFn} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ── Heat Map ──────────────────────────────────────────────────────────────────
const HeatMapCard: React.FC<{ spec: ChartSpec }> = ({ spec }) => {
  const { title, subtitle, rowLabels = [], colLabels = [], heatData = [] } = spec;
  const fmtKey = spec.format || 'number';
  const fmtFn = (v: number) => fmt(v, fmtKey);
  const allValues = heatData.flat().filter((v): v is number => v !== null && v !== undefined);
  const minVal = allValues.length ? Math.min(...allValues) : 0;
  const maxVal = allValues.length ? Math.max(...allValues) : 1;

  const getColor = (v: number | null) => {
    if (v === null || v === undefined) return '#f8f9fa';
    const t = maxVal === minVal ? 0.5 : (v - minVal) / (maxVal - minVal);
    return `rgb(${Math.round(219 - t * 155)},${Math.round(233 - t * 113)},${Math.round(246 - t * 79)})`;
  };

  return (
    <div style={{ ...CARD_STYLE, overflowX: 'auto' }}>
      <ChartHeader title={title} subtitle={subtitle} />
      <table style={{ borderCollapse: 'separate', borderSpacing: '3px', fontSize: '11px' }}>
        <thead>
          <tr>
            <th />
            {colLabels.map(l => (
              <th key={l} style={{ padding: '2px 6px', color: TEXT_SECONDARY, fontWeight: 500, textAlign: 'center', minWidth: 36 }}>{l}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {heatData.map((row, ri) => (
            <tr key={ri}>
              <td style={{ padding: '2px 8px 2px 0', color: TEXT_SECONDARY, fontWeight: 500, whiteSpace: 'nowrap', textAlign: 'right' }}>
                {rowLabels[ri] ?? ri}
              </td>
              {row.map((val, ci) => {
                const t = maxVal === minVal ? 0.5 : (((val ?? 0) as number) - minVal) / (maxVal - minVal);
                return (
                  <td key={ci} style={{
                    background: getColor(val as number | null), width: 36, height: 28, textAlign: 'center',
                    borderRadius: '4px', color: t > 0.6 ? '#fff' : TEXT_PRIMARY,
                    fontWeight: 500, cursor: 'default',
                  }} title={val !== null && val !== undefined ? fmtFn(val as number) : 'No data'}>
                    {val !== null && val !== undefined ? fmtFn(val as number) : '–'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Scatter Chart ─────────────────────────────────────────────────────────────
const ScatterChartCard: React.FC<{ spec: ChartSpec }> = ({ spec }) => {
  const { title, subtitle, data, xKey = 'x', yKey = 'y', xLabel, yLabel } = spec;

  const n = data.length;
  const sumX  = data.reduce((s, d) => s + Number(d[xKey] ?? 0), 0);
  const sumY  = data.reduce((s, d) => s + Number(d[yKey] ?? 0), 0);
  const sumXY = data.reduce((s, d) => s + Number(d[xKey] ?? 0) * Number(d[yKey] ?? 0), 0);
  const sumX2 = data.reduce((s, d) => s + Number(d[xKey] ?? 0) ** 2, 0);
  const slope     = n > 1 && (n * sumX2 - sumX ** 2) !== 0 ? (n * sumXY - sumX * sumY) / (n * sumX2 - sumX ** 2) : 0;
  const intercept = n > 1 ? (sumY - slope * sumX) / n : 0;
  const xVals = data.map(d => Number(d[xKey] ?? 0));
  const minX = Math.min(...xVals), maxX = Math.max(...xVals);
  const trendData = n > 1 ? [
    { [xKey]: minX, [yKey]: slope * minX + intercept },
    { [xKey]: maxX, [yKey]: slope * maxX + intercept },
  ] : [];

  return (
    <div style={CARD_STYLE}>
      <ChartHeader title={title} subtitle={subtitle} />
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart margin={{ top: 8, right: 24, bottom: 24, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRIDLINE} />
          <XAxis dataKey={xKey} type="number" name={xLabel || xKey} tick={{ fontSize: 11, fill: TEXT_SECONDARY }} tickLine={false}
            label={{ value: xLabel || xKey, position: 'insideBottom', offset: -12, fontSize: 11, fill: TEXT_MUTED }} />
          <YAxis dataKey={yKey} type="number" name={yLabel || yKey} tick={{ fontSize: 11, fill: TEXT_SECONDARY }} axisLine={false} tickLine={false}
            label={{ value: yLabel || yKey, angle: -90, position: 'insideLeft', fontSize: 11, fill: TEXT_MUTED }} width={52} />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} cursor={{ strokeDasharray: '3 3' }} />
          <Scatter data={data} fill={CATEGORICAL[0]} fillOpacity={0.7} />
          {trendData.length > 0 && (
            <Line data={trendData} dataKey={yKey} stroke="#e74c3c" strokeWidth={2} dot={false} strokeDasharray="6 3" />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

// ── Bullet Chart ─────────────────────────────────────────────────────────────
const BulletChartCard: React.FC<{ spec: ChartSpec; onDrilldown?: (label: string) => void | Promise<void> }> = ({ spec, onDrilldown }) => {
  const { title, subtitle, metrics = [] } = spec;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(460);
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => setContainerWidth(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const BAR_H = 32, ROW_H = 70, LABEL_W = 150;
  const CHART_W = Math.max(180, containerWidth - LABEL_W - 80);
  const totalH = metrics.length * ROW_H + 24;

  return (
    <div ref={containerRef} style={CARD_STYLE}>
      <ChartHeader title={title} subtitle={subtitle} />
      <svg width={containerWidth} height={totalH} style={{ overflow: 'visible' }}>
        {metrics.map((m, i) => {
          const y = i * ROW_H + 12;
          const cy = y + ROW_H / 2;
          // Auto-fix max: if missing/zero, use 120% of the largest of actual/target
          const safeMax = (m.max && m.max > 0) ? m.max : Math.max(m.actual || 0, m.target || 0) * 1.2 || 100;
          const scale = (v: number) => (v / safeMax) * CHART_W;
          const isGood = m.isPositiveGood !== false ? m.actual >= m.target : m.actual <= m.target;
          const barColor = isGood ? CATEGORICAL[0] : '#e74c3c';
          const bandColors = ['#f0f0f0', '#dde1e4', '#c8cdd2'];
          const fmtFn = m.format ? (v: number) => fmt(v, m.format!) : (v: number) => v.toLocaleString();
          const bands = [
            { from: 0, to: safeMax * 0.5 },
            { from: safeMax * 0.5, to: safeMax * 0.75 },
            { from: safeMax * 0.75, to: safeMax },
          ];

          return (
            <g key={m.label} onClick={() => onDrilldown?.(m.label)} style={{ cursor: onDrilldown ? 'pointer' : 'default' }}>
              {/* Invisible hit area for full row */}
              <rect x={0} y={cy - ROW_H / 2} width={containerWidth} height={ROW_H} fill="transparent" />
              <text x={LABEL_W - 10} y={cy + 4} textAnchor="end" fontSize={12} fill={onDrilldown ? '#5b21b6' : TEXT_PRIMARY} fontWeight={500}>{m.label}</text>
              {bands.map((band, bi) => (
                <rect key={bi} x={LABEL_W + scale(band.from)} y={cy - 14}
                  width={scale(band.to - band.from)} height={28} fill={bandColors[bi]} rx={2} />
              ))}
              <rect x={LABEL_W} y={cy - BAR_H / 2} width={scale(Math.min(m.actual, safeMax))} height={BAR_H} fill={barColor} rx={2} />
              <rect x={LABEL_W + scale(m.target) - 1.5} y={cy - 16} width={3} height={32} fill={TEXT_PRIMARY} rx={1} />
              <text x={LABEL_W + scale(Math.min(m.actual, safeMax)) + 6} y={cy + 4} fontSize={11} fill={TEXT_SECONDARY} fontWeight={600}>
                {fmtFn(m.actual)}
              </text>
              <text x={LABEL_W + scale(m.target)} y={cy - 20} fontSize={9} fill={TEXT_MUTED} textAnchor="middle">Target</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// ── Sparkline Table ───────────────────────────────────────────────────────────
const SparklineTableCard: React.FC<{ spec: ChartSpec }> = ({ spec }) => {
  const { title, subtitle, rows = [] } = spec;

  return (
    <div style={{ ...CARD_STYLE, padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '20px 24px 12px' }}>
        <ChartHeader title={title} subtitle={subtitle} />
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ background: '#f8f9fa', borderBottom: `1px solid ${BORDER}` }}>
            {['Metric', 'Current', 'vs. Target', 'vs. Prior', 'Trend'].map((h, i) => (
              <th key={h} style={{ padding: '8px 16px', textAlign: i === 0 ? 'left' : 'right', fontSize: '11px', fontWeight: 600, color: TEXT_SECONDARY, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const vtGood = row.isPositiveGood !== false ? (row.vsTarget ?? 0) >= 0 : (row.vsTarget ?? 0) <= 0;
            const vpGood = row.isPositiveGood !== false ? (row.vsPrior ?? 0) >= 0 : (row.vsPrior ?? 0) <= 0;
            const sparkColor = vpGood ? '#2ecc71' : '#e74c3c';
            return (
              <tr key={i} style={{ borderBottom: `1px solid ${GRIDLINE}`, background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={{ padding: '10px 16px', fontWeight: 500, color: TEXT_PRIMARY }}>{row.label}</td>
                <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 700, color: TEXT_PRIMARY, fontVariantNumeric: 'tabular-nums' }}>{row.value}</td>
                <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                  {row.vsTarget != null ? (
                    <span style={{ color: vtGood ? '#2ecc71' : '#e74c3c', fontWeight: 600, fontSize: '12px' }}>
                      {(row.vsTarget ?? 0) >= 0 ? '▲' : '▼'} {Math.abs(row.vsTarget ?? 0).toFixed(1)}%
                    </span>
                  ) : <span style={{ color: TEXT_MUTED }}>—</span>}
                </td>
                <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                  {row.vsPrior != null ? (
                    <span style={{ color: vpGood ? '#2ecc71' : '#e74c3c', fontWeight: 600, fontSize: '12px' }}>
                      {(row.vsPrior ?? 0) >= 0 ? '▲' : '▼'} {Math.abs(row.vsPrior ?? 0).toFixed(1)}%
                    </span>
                  ) : <span style={{ color: TEXT_MUTED }}>—</span>}
                </td>
                <td style={{ padding: '6px 16px', textAlign: 'right' }}>
                  {row.sparkData && row.sparkData.length > 0 ? (
                    <div style={{ width: 80, height: 32, display: 'inline-block' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={row.sparkData} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                          <Line type="monotone" dataKey="v" stroke={sparkColor} strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : <span style={{ color: TEXT_MUTED }}>—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Insight Callout ───────────────────────────────────────────────────────────
const InsightCallout: React.FC<{ insight: InsightSpec; index: number }> = ({ insight, index }) => (
  <div style={{ ...CARD_STYLE, borderLeft: `4px solid ${CATEGORICAL[index % CATEGORICAL.length]}` }}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', gap: '14px' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: TEXT_SECONDARY, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', marginTop: '3px' }}>What</span>
        <span style={{ fontSize: '15px', color: TEXT_PRIMARY, lineHeight: 1.6 }}>{insight.what}</span>
      </div>
      <div style={{ display: 'flex', gap: '14px' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: TEXT_SECONDARY, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', marginTop: '3px' }}>So What</span>
        <span style={{ fontSize: '15px', color: TEXT_PRIMARY, lineHeight: 1.6 }}>{insight.soWhat}</span>
      </div>
      <div style={{ display: 'flex', gap: '14px' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: '#4e79a7', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', marginTop: '3px' }}>Now What</span>
        <span style={{ fontSize: '15px', color: TEXT_PRIMARY, fontWeight: 500, lineHeight: 1.6 }}>{insight.nowWhat}</span>
      </div>
    </div>
  </div>
);

// ── Drill-down helper: exclude known segments (for "Others" category) ─────────
function filterRawDataExclude(rawData: RawDataFile[], filterColumn: string, excludeValues: string[]): DrillDownData | null {
  for (const file of rawData) {
    for (const sheet of file.sheets) {
      const colIdx = sheet.headers.findIndex(h => h.toLowerCase() === filterColumn.toLowerCase());
      if (colIdx === -1) continue;
      const rows = sheet.rows.filter(row => {
        const v = String((row as unknown[])[colIdx] ?? '').toLowerCase();
        return v && !excludeValues.includes(v);
      });
      if (rows.length > 0)
        return { title: `Others — ${rows.length} records`, columns: sheet.headers, rows: rows.slice(0, 100) };
    }
  }
  return null;
}

// ── Drill-down helper: filter rawData by column=value ─────────────────────────
function filterRawData(rawData: RawDataFile[], filterColumn: string, label: string, filterType?: string): DrillDownData | null {
  const lLabel = label.toLowerCase().trim();
  const normalise = (s: string) => s.toLowerCase().trim().replace(/[\s_\-/\\]+/g, ' ');
  const nLabel = normalise(label);
  // numeric: "123.0" → "123", strip currency symbols
  const numLabel = lLabel.replace(/[^0-9.]/g, '');
  const numericSuffix = label.match(/\d+$/)?.[0];

  const matchesValue = (cellVal: string): boolean => {
    const v = cellVal.trim();
    const lv = v.toLowerCase();
    const nv = normalise(v);
    if (filterType === 'year') return v.startsWith(label) || lv.startsWith(lLabel);
    if (filterType === 'month') return lv.includes(lLabel) || lLabel.includes(lv);
    // exact
    if (lv === lLabel) return true;
    // normalised (spaces/dashes/underscores)
    if (nv === nLabel) return true;
    // numeric suffix match: "Period 1" → "1"
    if (numericSuffix && lv === numericSuffix) return true;
    // strip non-numeric for number columns: "$1,234.00" → "1234" matches "1234"
    if (numLabel && numLabel.length > 0) {
      const nvNum = lv.replace(/[^0-9.]/g, '');
      if (nvNum === numLabel) return true;
      // float: "123.0" === "123"
      if (parseFloat(nvNum) === parseFloat(numLabel) && !isNaN(parseFloat(numLabel))) return true;
    }
    // partial: label contains cell value or vice versa (min 3 chars)
    if (lLabel.length >= 3 && lv.length >= 3 && (lv.includes(lLabel) || lLabel.includes(lv))) return true;
    return false;
  };

  for (const file of rawData) {
    for (const sheet of file.sheets) {
      // Pass 1: exact column name
      let colIdx = sheet.headers.findIndex(h => h.toLowerCase().trim() === filterColumn.toLowerCase().trim());
      // Pass 1b: normalised column name
      if (colIdx === -1) {
        colIdx = sheet.headers.findIndex(h => normalise(h) === normalise(filterColumn));
      }
      // Pass 1c: partial column name
      if (colIdx === -1) {
        const parts = filterColumn.toLowerCase().split(/[_\s]/);
        colIdx = sheet.headers.findIndex(h => parts.some(p => p.length > 2 && h.toLowerCase().includes(p)));
      }

      if (colIdx !== -1) {
        const rows = sheet.rows.filter(row => matchesValue(String((row as unknown[])[colIdx] ?? '')));
        if (rows.length > 0)
          return { title: `${label} — ${rows.length} records`, columns: sheet.headers, rows: rows.slice(0, 200) };
      }

      // Pass 2: scan ALL columns for matching value
      for (let ci = 0; ci < sheet.headers.length; ci++) {
        if (ci === colIdx) continue;
        const rows = sheet.rows.filter(row => matchesValue(String((row as unknown[])[ci] ?? '')));
        if (rows.length > 0)
          return { title: `${label} — ${rows.length} records`, columns: sheet.headers, rows: rows.slice(0, 200) };
      }
    }
  }
  return null;
}

// ── Main Dashboard Renderer ───────────────────────────────────────────────────
const DashboardRenderer: React.FC<{ spec: DashboardSpecType; forceTwoColumn?: boolean; rawData?: RawDataFile[]; onDrilldownFetch?: (params: { column: string; value: string; filterType?: string; excludeValues?: string[] }) => Promise<DrillDownData | null> }> = ({ spec, forceTwoColumn = false, rawData, onDrilldownFetch }) => {
  const { title, subtitle, summary = [], kpis = [], charts = [], insights = [] } = spec;
  const FULL_WIDTH_TYPES = forceTwoColumn ? [] : ['stacked_bar', 'waterfall', 'heatmap', 'bullet', 'sparkline_table'];
  const [drilldown, setDrilldown] = React.useState<DrillDownData | null>(null);
  const [drillMsg, setDrillMsg] = React.useState<string | null>(null);
  const [drillLoading, setDrillLoading] = React.useState(false);

  const makeDrilldown = (chart: ChartSpec) => {
    if (!onDrilldownFetch && !rawData?.length) return undefined;

    // If filterColumn missing, try to infer from rawData by matching chart bar names to column values
    if (!chart.filterColumn && rawData?.length) {
      const chartNames = (chart.data || chart.metrics || []).map((d: any) =>
        String(d.name || d.label || '').toLowerCase().trim()
      ).filter(Boolean);

      if (chartNames.length > 0) {
        for (const file of rawData) {
          for (const sheet of file.sheets) {
            for (let ci = 0; ci < sheet.headers.length; ci++) {
              const colVals = sheet.rows.map(r => String((r as any[])[ci] ?? '').toLowerCase().trim());
              const uniqueVals = [...new Set(colVals)];
              const matches = chartNames.filter(n => uniqueVals.some(v => v === n || v.includes(n) || n.includes(v))).length;
              if (matches >= Math.max(1, Math.floor(chartNames.length * 0.4))) {
                chart.filterColumn = sheet.headers[ci];
                console.log(`[DrillDown] inferred filterColumn="${chart.filterColumn}" for chart "${chart.title}"`);
                break;
              }
            }
            if (chart.filterColumn) break;
          }
          if (chart.filterColumn) break;
        }
      }
    }

    // Still no filterColumn after inference — show message
    if (!chart.filterColumn) {
      return (_label: string) => {
        setDrillMsg('Drill-down not available for this chart — bars represent calculated metrics, not individual row values.');
        setTimeout(() => setDrillMsg(null), 4000);
      };
    }

    return async (label: string) => {
      const col = chart.filterColumn ?? '';
      console.log('[DrillDown] clicked:', { label, col, filterType: chart.filterType, hasRawData: !!rawData?.length, hasServerFetch: !!onDrilldownFetch });
      const isOthers = /^(all\s+)?others?$/i.test(label.trim());
      const excludeValues = isOthers
        ? (chart.data || []).map(d => String(d.name ?? '')).filter(n => n && !/^(all\s+)?others?$/i.test(n))
        : undefined;

      // Try server-side first (fresh sessions with cached file)
      if (onDrilldownFetch && col) {
        setDrillLoading(true);
        let serverResult: DrillDownData | null = null;
        try {
          serverResult = await onDrilldownFetch({
            column: col,
            value: isOthers ? 'others' : label,
            filterType: chart.filterType,
            excludeValues,
          });
        } catch {
          // 404 = file not cached (history session) — fall through to rawData
        } finally {
          setDrillLoading(false);
        }
        if (serverResult) { setDrilldown(serverResult); return; }
      }

      // Fallback: client-side filter from rawData (history sessions)
      console.log('[DrillDown] rawData files:', rawData?.map(f => ({ name: f.fileName, sheets: f.sheets.map(s => ({ sheet: s.sheetName, cols: s.headers.slice(0,6), rows: s.rows.length })) })));
      if (isOthers && col && rawData?.length) {
        const namedSegments = (chart.data || []).map(d => String(d.name ?? '').toLowerCase()).filter(n => n && !/^(all\s+)?others?$/i.test(n));
        const result = filterRawDataExclude(rawData, col, namedSegments);
        if (result) { setDrilldown(result); return; }
      }
      if (rawData?.length) {
        const result = filterRawData(rawData, col, label, chart.filterType);
        console.log('[DrillDown] filterRawData result:', result ? `${result.rows.length} rows` : 'null');
        if (result) { setDrilldown(result); return; }
        setDrillMsg(`No matching records found for "${label}" — data may be aggregated differently in your file.`);
      } else {
        setDrillMsg(`Drill-down unavailable for this saved session. Re-upload your file and re-run the analysis to enable it.`);
      }
      setTimeout(() => setDrillMsg(null), 5000);
    };
  };

  const renderChart = (chart: ChartSpec, i: number) => {
    const onDrilldown = makeDrilldown(chart);
    switch (chart.type) {
      case 'bar':             return <BarChartCard         key={i} spec={chart} onDrilldown={onDrilldown} />;
      case 'line':            return <LineChartCard        key={i} spec={chart} />;
      case 'donut':           return <DonutChartCard       key={i} spec={chart} onDrilldown={onDrilldown} />;
      case 'pop_bar':         return <PoPBarChartCard      key={i} spec={chart} />;
      case 'stacked_bar':     return <StackedBarChartCard  key={i} spec={chart} onDrilldown={onDrilldown} />;
      case 'waterfall':       return <WaterfallChartCard   key={i} spec={chart} onDrilldown={onDrilldown} />;
      case 'heatmap':         return <HeatMapCard          key={i} spec={chart} />;
      case 'scatter':         return <ScatterChartCard     key={i} spec={chart} />;
      case 'bullet':          return <BulletChartCard      key={i} spec={chart} onDrilldown={onDrilldown} />;
      case 'sparkline_table': return <SparklineTableCard   key={i} spec={chart} />;
      default:                return null;
    }
  };

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Dashboard Header */}
      {title && (
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: TEXT_PRIMARY, margin: 0 }}>{title}</h2>
          {subtitle && <p style={{ fontSize: '12px', color: TEXT_SECONDARY, margin: '4px 0 0' }}>{subtitle}</p>}
        </div>
      )}

      {/* Data Summary Pills */}
      {summary.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {summary.map((s, i) => (
            <div key={i} style={{ background: '#f8f9fa', borderRadius: '10px', padding: '10px 16px', border: `1px solid ${BORDER}` }}>
              <div style={{ fontSize: '10px', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>{s.label}</div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: TEXT_PRIMARY }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* KPI Row */}
      {kpis.length > 0 && (
        <div data-export-section="KPI Overview" style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(180px, 1fr))`, gap: '16px', padding: '4px', background: '#fff' }}>
          {kpis.map((kpi, i) => <KPICard key={i} {...kpi} />)}
        </div>
      )}

      {/* Charts — 2-column grid, full-width for complex types or lone charts */}
      {charts.length > 0 && (() => {
        const spans: boolean[] = [];
        let col = 0;
        for (let i = 0; i < charts.length; i++) {
          const isFull = FULL_WIDTH_TYPES.includes(charts[i].type);
          if (isFull) {
            spans.push(true);
            col = 0;
          } else {
            const next = charts[i + 1];
            const hasPartner = next && !FULL_WIDTH_TYPES.includes(next.type);
            if (col === 0 && !hasPartner) {
              spans.push(true);
              col = 0;
            } else {
              spans.push(false);
              col = col === 0 ? 1 : 0;
            }
          }
        }
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
            {charts.map((chart, i) => (
              <div key={i} data-export-section={chart.title}
                style={spans[i] ? { gridColumn: '1 / -1' } : {}}>
                {renderChart(chart, i)}
              </div>
            ))}
          </div>
        );
      })()}

      {/* Insight Callouts */}
      {insights.length > 0 && (
        <div data-export-section="Key Insights">
          <div style={{ fontSize: '11px', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
            Key Insights
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {insights.map((ins, i) => <InsightCallout key={i} insight={ins} index={i} />)}
          </div>
        </div>
      )}

      {/* Data freshness footer */}
      <div style={{ fontSize: '10px', color: TEXT_MUTED, textAlign: 'right' }}>
        Generated by Jarvis Intelligence · Analysis based on uploaded dataset
      </div>

      {drilldown && <DrillDownModal data={drilldown} onClose={() => setDrilldown(null)} />}
      {drillLoading && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1002, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px 32px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ width: '20px', height: '20px', border: '3px solid #7c3aed', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '14px', color: '#1e1b4b', fontWeight: 500 }}>Loading drill-down data…</span>
          </div>
        </div>
      )}
      {drillMsg && (
        <div style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', zIndex: 1001, background: '#1e1b4b', color: '#e0e7ff', padding: '10px 20px', borderRadius: '8px', fontSize: '13px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
          {drillMsg}
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default DashboardRenderer;
